import { useState, useEffect } from 'react';
import { customerService } from '../services/customerService';
import { useAuth } from '../contexts/AuthContext';

export const useCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    if (user?.companyId) {
      loadCustomers();
    } else if (user?.role === 'admin') {
      loadAllCustomers();
    } else {
      setCustomers([]);
      setLoading(false);
    }
  }, [user]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!user?.companyId) {
        setCustomers([]);
        return;
      }
      
      const data = await customerService.getByCompany(user.companyId);
      setCustomers(data);
    } catch (err) {
      setError(err.message);
      console.error('Erro ao carregar clientes:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAllCustomers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await customerService.getAll();
      setCustomers(data);
    } catch (err) {
      setError(err.message);
      console.error('Erro ao carregar todos os clientes:', err);
    } finally {
      setLoading(false);
    }
  };

  const addCustomer = async (customerData) => {
    try {
      // Verificar se o usuário tem companyId
      if (!user?.companyId) {
        throw new Error('Usuário não está associado a uma empresa');
      }

      const newCustomer = await customerService.create({
        name: customerData.name,
        cpf: customerData.cpf,
        phone: customerData.phone,
        email: customerData.email,
        birthDate: customerData.birthDate,
        companyId: user.companyId
      });
      
      setCustomers(prev => [...prev, newCustomer]);
      return newCustomer;
    } catch (err) {
      setError(err.message);
      console.error('Erro ao adicionar cliente:', err);
      throw err;
    }
  };

  const updateCustomer = async (customerId, customerData) => {
    try {
      const updatedCustomer = await customerService.update(customerId, customerData);
      setCustomers(prev => 
        prev.map(customer => 
          customer.id === customerId ? { ...customer, ...updatedCustomer } : customer
        )
      );
      return updatedCustomer;
    } catch (err) {
      setError(err.message);
      console.error('Erro ao atualizar cliente:', err);
      throw err;
    }
  };

  const deleteCustomer = async (customerId) => {
    try {
      await customerService.delete(customerId);
      setCustomers(prev => prev.filter(customer => customer.id !== customerId));
    } catch (err) {
      setError(err.message);
      console.error('Erro ao deletar cliente:', err);
      throw err;
    }
  };

  const findCustomerByCpf = async (cpf) => {
    try {
      const customer = await customerService.getByCpf(cpf);
      return customer;
    } catch (err) {
      setError(err.message);
      console.error('Erro ao buscar cliente por CPF:', err);
      throw err;
    }
  };

  const findCustomerByDocument = async (document) => {
    try {
      const customer = await customerService.getByDocument(document);
      return customer;
    } catch (err) {
      setError(err.message);
      console.error('Erro ao buscar cliente por documento:', err);
      throw err;
    }
  };

  const updateCustomerPoints = (customerId, pointsToAdd) => {
    setCustomers(prev => 
      prev.map(customer => 
        customer.id === customerId 
          ? { ...customer, points: (customer.points || 0) + pointsToAdd }
          : customer
      )
    );
  };

  const setCustomerPoints = (customerId, newPoints) => {
    setCustomers(prev => 
      prev.map(customer => 
        customer.id === customerId 
          ? { ...customer, points: newPoints }
          : customer
      )
    );
  };

  return {
    customers,
    loading,
    error,
    addCustomer,
    updateCustomer,
    deleteCustomer,
    findCustomerByCpf,
    findCustomerByDocument,
    updateCustomerPoints,
    setCustomerPoints,
    reload: loadCustomers,
    loadCustomers, // Adicionar método explícito para facilitar uso
    fetchCustomers: loadCustomers // Alias para compatibilidade
  };
};